import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDietaryRestrictions } from './useDietaryRestrictions';
import './DietaryRestrictions.css';

interface DietaryRestrictionsProps {
  selected: string[];
  onToggle: (restriction: string) => void;
  onAddCustom: (restriction: string) => boolean;
  onRemoveCustom: (restriction: string) => void;
}

const DietaryRestrictions: React.FC<DietaryRestrictionsProps> = ({
  selected,
  onToggle,
  onAddCustom,
  onRemoveCustom,
}) => {
  const { t } = useTranslation();
  
  const predefinedOptions = [
    'Vegetarian',
    'Vegan',
    'Gluten Free',
    'Dairy Free',
    'Nut Free',
    'Halal',
    'Kosher',
    'No Pork',
    'No Beef',
    'Low Sodium',
    'Diabetic',
  ];

  // Derive custom restrictions: items in selected that are not in predefinedOptions
  const custom = selected.filter((restriction) => !predefinedOptions.includes(restriction));
  // Derive predefined selections: items in selected that are in predefinedOptions
  const predefinedSelected = selected.filter((restriction) => predefinedOptions.includes(restriction));

  const {
    customInput,
    setCustomInput,
    handleToggle,
    handleAddCustom,
    handleRemoveCustom,
    handleKeyPress,
  } = useDietaryRestrictions({
    selected,
    onToggle,
    onAddCustom,
    onRemoveCustom,
  });

  return (
    <div className="dietary-restrictions">
      <h3 className="dietary-title">{t('form.answersSection.dietaryRestrictions.title')}</h3>
      <p className="dietary-description">
        {t('form.answersSection.dietaryRestrictions.description')}
      </p>

      <div className="options-section">
        <h4 className="options-title">
          {t('form.answersSection.dietaryRestrictions.optionsTitle')}
        </h4>
        <div className="restrictions-pills" role="group" aria-label={t('form.answersSection.dietaryRestrictions.optionsTitle')}>
          {predefinedOptions.map((option) => {
            const isSelected = predefinedSelected.includes(option);
            return (
              <button
                key={option}
                type="button"
                className={`restriction-pill ${isSelected ? 'selected' : ''}`}
                onClick={() => handleToggle(option)}
                aria-pressed={isSelected}
                aria-label={`${option}${isSelected ? ', selected' : ', not selected'}`}
              >
                {isSelected && <span className="pill-check" aria-hidden="true">✓</span>}
                <span className="pill-text">{option}</span>
              </button>
            );
          })}
          {custom.map((restriction) => (
            <div key={restriction} className="restriction-pill custom selected">
              <span className="pill-check" aria-hidden="true">✓</span>
              <span className="pill-text">{restriction}</span>
              <button
                type="button"
                className="remove-pill"
                onClick={() => handleRemoveCustom(restriction)}
                aria-label={`Remove ${restriction}`}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="custom-restriction-input">
        <label htmlFor="custom-restriction-input" className="sr-only">
          {t('form.answersSection.dietaryRestrictions.customPlaceholder')}
        </label>
        <input
          id="custom-restriction-input"
          type="text"
          className="form-input"
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t('form.answersSection.dietaryRestrictions.customPlaceholder')}
          aria-describedby="custom-restriction-hint"
        />
        <span id="custom-restriction-hint" className="sr-only">
          {t('form.answersSection.dietaryRestrictions.description')}
        </span>
        <button
          type="button"
          className="add-button"
          onClick={handleAddCustom}
          disabled={!customInput.trim()}
          aria-label={t('form.answersSection.dietaryRestrictions.addButton')}
        >
          {t('form.answersSection.dietaryRestrictions.addButton')}
        </button>
      </div>
    </div>
  );
};

export default DietaryRestrictions;

